import React, { useMemo } from 'react';
import { View, Text, Image, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { LinkedAccount } from '@/types/residents/home';
import { UserType } from '@/types/residents/user';
import { WebConfirmModal } from '@/components/common/modals';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useAppSelector } from '@/store';
import { formatConnectedSince } from '@/utils/residents/date';
import { linkedAccountsStrings } from '@/constants/residents/strings';
import RNButton from '@/components/common/residents/desktop/Button';
import { getDisplayName } from '@/utils/residents/utils';

interface DesktopLinkedAccountCardProps {
  account: LinkedAccount;
  onRemove: (account: LinkedAccount) => void;
}

export const DesktopLinkedAccountCard: React.FC<DesktopLinkedAccountCardProps> = ({ account, onRemove }) => {
  const [showConfirm, setShowConfirm] = React.useState(false);
  const displayName = useMemo(() => getDisplayName(account.user.firstName, account.user.lastName) || account.user.email, [account]);

  // Prefer marketing name from store (matches web), fallback to the account's property name
  const marketingNameFromStore = useAppSelector((s) => s.auth.user?.property?.marketingName);
  const propertyDisplayName = marketingNameFromStore || account.property?.name;

  const connectedSince = useMemo(() => formatConnectedSince(account.connectedSince), [account.connectedSince]);

  return (
    <View style={styles.card}>
      <WebConfirmModal
        visible={Platform.OS === 'web' && showConfirm}
        title={'Remove Recipient'}
        message={`Are you sure you want to remove connection to ${displayName}`}
        confirmText={'Remove'}
        cancelText={'Cancel'}
        onConfirm={() => {
          setShowConfirm(false);
          onRemove(account);
        }}
        onCancel={() => setShowConfirm(false)}
      />
      <View style={styles.leftSection}>
        <View style={styles.topRow}>
          <View style={styles.avatar}>
            {account.user.photoUrl ? (
              <Image source={{ uri: account.user.photoUrl }} style={styles.avatarImg} />
            ) : (
              <RESIDENTS_SVG_COMPONENTS.PROFILE_PLACEHOLDER width={'50%'} height={'50%'} />
            )}
          </View>

          <View style={styles.details}>
            <Text style={styles.name} numberOfLines={1}>{displayName}</Text>
            {account.user.type === UserType.RESIDENT && !!propertyDisplayName && (
              <Text style={styles.propertyName} numberOfLines={1}>{propertyDisplayName}</Text>
            )}
            <View style={styles.row}>
              <RESIDENTS_SVG_COMPONENTS.PAY width={20} height={20} style={{ marginRight: 8 }} />
              <Text style={styles.rowText} numberOfLines={1}>
                {account.user.type === UserType.RESIDENT ? linkedAccountsStrings.cell.capabilityResident : linkedAccountsStrings.cell.capabilityGuest}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.actionsRow}>
          <RNButton title={linkedAccountsStrings.cell.removeTitle} variant="link" onPress={() => setShowConfirm(true)} />
          {!!connectedSince && (
            <Text style={styles.connectedSince}>{connectedSince}</Text>
          )}
        </View>
      </View>

      <View style={styles.rightSection}>
        {account.property?.photoUrl ? (
          <Image source={{ uri: account.property.photoUrl }} style={styles.propertyImg} resizeMode="cover" />
        ) : (
          <View style={styles.propertyImg}>
            <RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER width={'100%'} height={'100%'} />
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'hidden',
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    borderRadius: 16,
  },
  leftSection: {
    flex: 1,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 20,
    paddingRight: 16,
    borderRightWidth: 0,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#2f2f2f',
  },
  rightSection: {
    width: 250,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 12,
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.CIRCLE_ICON_BACKGROUND,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    marginRight: 16,
  },
  avatarImg: {
    width: '100%',
    height: '100%',
  },
  avatarFallback: {
    color: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    fontWeight: '600',
    fontSize: 20,
  },
  details: {
    marginLeft: 0,
    flex: 1,
  },
  name: {
    fontSize: 18,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 6,
  },
  propertyName: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 6,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  rowText: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 12,
  },
  connectedSince: {
    fontSize: 12,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    marginLeft: 24,
  },
  removeBtn: {
    paddingVertical: 6,
  },
  removeText: {
    color: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
    fontSize: 14,
  },
  propertyImg: {
    width: '100%',
    height: 140,
    borderRadius: 8,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  propertyPlaceholder: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    opacity: 0.2,
  },
});

export default DesktopLinkedAccountCard;


